<template>
    <div class="selection:bg-brand-500 selection:text-white pb-20 overflow-x-hidden">
        <!-- Hero / Featured Post -->
        <header class="relative pt-20 pb-24 sm:pt-24 sm:pb-16 border-b border-app">
            <div
                class="absolute inset-0 -z-10 bg-[radial-gradient(40%_40%_at_50%_50%,rgba(var(--core-brand-500),0.03)_0%,transparent_100%)]">
            </div>
            <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div class="text-center mb-16">
                    <h1 class="text-base font-semibold text-brand-500 uppercase tracking-widest mb-6">The Dephics Blog
                    </h1>
                    <p
                        class="mx-auto max-w-4xl text-5xl font-extrabold tracking-tight text-header sm:text-7xl leading-[1.1]">
                        Insights for the <span
                            class="bg-gradient-to-r to-brand-500 via-primary-500 from-secondary-500 bg-clip-text text-transparent font-extrabold">Digital
                            Frontier</span>.
                    </p>
                </div>


            </div>
        </header>

        <!-- Articles Grid -->
        <main class="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
            <div class="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
                <div>
                    <h3 class="text-3xl font-bold text-header">Latest Articles</h3>
                    <p class="text-body/80 mt-2">The latest news, tutorials, and analysis from our team.</p>
                </div>
                <div class="flex gap-2">
                    <button v-for="cat in ['All', 'Product', 'Engineering', 'Security']" :key="cat"
                        :class="cat === 'All' ? 'bg-brand-500 text-white' : 'bg-card text-header border border-app hover:bg-muted'"
                        class="px-4 py-2 rounded-xl text-sm font-bold transition-all">{{ cat }}</button>
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                <template v-for="post in posts" :key="post.id">
                    <NuxtLink :to="{ name: 'BlogSingle', params: { slug: post.slug } }"
                        class="group flex flex-col cursor-pointer">
                        <div class="relative aspect-[16/10] rounded-3xl overflow-hidden border border-app bg-card mb-6">
                            <img :src="post.imageUrl" :alt="post.title"
                                class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                            <div class="absolute top-4 left-4">
                                <span
                                    class="px-3 py-1 rounded-full bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm text-header text-[10px] font-bold uppercase shadow-sm">
                                    {{ post.category ? post.category.title : "" }}
                                </span>
                            </div>
                        </div>
                        <div
                            class="flex items-center gap-2 mb-3 text-[10px] font-bold text-body/80 uppercase tracking-widest">
                            <span>{{ $filter.day(post.createdAt) }}</span>
                            <span>•</span>
                            <span>{{ $filter.day(post.updatedAt) }} updated</span>
                        </div>
                        <h4
                            class="text-xl font-bold text-header mb-4 group-hover:text-brand-500 transition-colors line-clamp-2 leading-snug">
                            {{ post.title }}</h4>
                        <!-- <p class="text-body/80 text-sm leading-relaxed mb-6 line-clamp-3">{{ post.excerpt }}</p> -->
                        <!-- <div class="mt-auto pt-6 border-t border-app flex items-center justify-between">
                            <div class="flex items-center gap-2">
                                <img :src="post.authorImg" class="h-6 w-6 rounded-full" />
                                <span class="text-xs font-bold text-header">{{ post.author }}</span>
                            </div>
                            <ArrowUpRight
                                class="h-5 w-5 text-body/80 transition-all group-hover:text-brand-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                        </div> -->
                    </NuxtLink>
                </template>
            </div>

            <!-- Pagination -->
            <div class="mt-24 flex items-center justify-center gap-4">
                <button
                    class="h-10 px-4 rounded-xl border border-app text-body/80 hover:text-header transition-all flex items-center gap-2 text-sm font-bold">
                    <ChevronLeft class="h-4 w-4" /> Previous
                </button>
                <div class="flex gap-2">
                    <button class="h-10 w-10 rounded-xl bg-brand-500 text-white font-bold text-sm">1</button>
                    <button
                        class="h-10 w-10 rounded-xl border border-app text-header hover:bg-muted font-bold text-sm transition-all">2</button>
                    <button
                        class="h-10 w-10 rounded-xl border border-app text-header hover:bg-muted font-bold text-sm transition-all">3</button>
                </div>
                <button
                    class="h-10 px-4 rounded-xl border border-app text-body/80 hover:text-header transition-all flex items-center gap-2 text-sm font-bold">
                    Next
                    <ChevronRight class="h-4 w-4" />
                </button>
            </div>
        </main>

        <!-- Newsletter Section -->
        <section class="py-24 relative overflow-hidden">
            <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div
                    class="bg-gradient-to-br from-brand-600 to-primary-700 rounded-[50px] p-12 lg:p-20 relative overflow-hidden text-center text-white shadow-2xl">
                    <div class="absolute top-0 right-0 p-20 opacity-10">
                        <Mail class="h-64 w-64" />
                    </div>
                    <div class="relative z-10 max-w-3xl mx-auto">
                        <h2 class="text-4xl font-extrabold mb-6">Stay ahead of the curve.</h2>
                        <p class="text-white/80 text-lg mb-10 leading-relaxed">Join our weekly newsletter to receive the
                            latest insights, product updates, and industry analysis directly to your inbox.</p>
                        <div class="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
                            <input type="email" placeholder="Enter your email address"
                                class="flex-1 rounded-2xl bg-white/10 border border-white/20 px-6 py-4 text-white placeholder:text-white/60 focus:bg-white/20 focus:outline-none transition-all" />
                            <button
                                class="bg-white text-brand-600 px-8 py-4 rounded-2xl font-bold hover:bg-slate-50 transition-all active:scale-95 shadow-xl">Subscribe
                                Now</button>
                        </div>
                        <p class="mt-6 text-xs text-white/40">We respect your privacy. Unsubscribe at any time.</p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import {
    ArrowUpRight,
    ChevronLeft,
    ChevronRight,
    Mail
} from 'lucide-vue-next'
import { Post } from '~/types';

definePageMeta({ name: 'Blog' });

const { $api } = useNuxtApp();

const fetchData = async (): Promise<Post[]> => {
    var wherePage = `status:{equalTo:"published"},type:{equalTo:"blog"}`;
    var where = `where:{${wherePage}}`;

    var query = `query GetPosts {
        posts(${where},) {
            id,
            postTypeId,
            postCategoryId,
            slug,
            title,
            subTitle,
            imageUrl,
            content,
            contentStyle,
            contentJs,
            contentData,
            sender,
            recipient,
            type,
            status,
            createdAt,
            updatedAt,
            postType { id, title },
            category: postCategory { id, title },
        }
    }`;

    var res = await $api.graphql(query, {}, "/graphql");

    return (res.data && res.data.posts) ? res.data.posts : [];
}

// Fetched during the server render; client-side navigation fills the list when it arrives.
const { data: posts } = useAsyncData('blog-posts', fetchData, { lazy: true, default: () => [] });
</script>

<style scoped>
.bg-card {
    background-color: rgb(var(--core-card-bg));
}

.bg-app {
    background-color: rgb(var(--core-app-bg));
}

.border-app {
    border-color: rgb(var(--core-app-border));
}

.text-header {
    color: rgb(var(--core-header-text));
}

.text-muted {
    color: rgb(var(--core-muted-text));
}
</style>
